import { Injectable } from '@angular/core'
import { AuthService } from '@app/core/auth/services/auth.service'
import { MessageService } from 'primeng/api'
import { Observable } from 'rxjs'
import { environment } from 'src/config/environments/environment'
const SERVER_URL = environment.serverUrl

type Messages = {
  success?: string
  error?: string
}
type ParseMode = 'json' | 'blob' | 'url' | 'blob_json'

type Options = {
  messages?: Messages
  silence?: boolean
  headers?: Record<string, string>
  parseMode: ParseMode
}

type FetchResult = {
  success?: boolean
  error?: unknown
  errorText?: string
  data?: unknown
}

@Injectable({
  providedIn: 'root',
})
export class FetchService {
  constructor(private authService: AuthService, private messageService: MessageService) {}
  _headers(data: Record<string, string>): Record<string, string> {
    if (!this.authService.user) {
      return data
    }
    return {
      ...data,
      Authorization: `Bearer ${this.authService.getToken()}`,
    }
  }
  _request(url: string, requestOptions: RequestInit, options: Options) {
    const messages = options.messages ? options.messages : {}
    const controller = new AbortController()
    requestOptions.signal = controller.signal
    return {
      abort: () => controller.abort(),
      response: fetch(SERVER_URL + url, requestOptions)
        .then((response) => {
          return (async () => {
            let result: Response | Blob | FetchResult
            if (response.status > 208) {
              const data = (await response.json()) as { message?: string }
              result = {
                success: false,
                error: data,
                errorText: data.message,
              }
            } else {
              if (options.parseMode == 'blob' || options.parseMode == 'url' || options.parseMode == 'blob_json') {
                const resultBlob = await response.blob()

                if (options.parseMode == 'url') {
                  result = {
                    success: true,
                    data: window.URL.createObjectURL(resultBlob),
                  }
                } else if (options.parseMode == 'blob_json') {
                  const text = await resultBlob.text()
                  try {
                    const parsed = JSON.parse(text) as unknown
                    result = {
                      success: true,
                      data: parsed,
                    }
                  } catch (e) {
                    result = {
                      success: false,
                      error: e,
                      errorText: (e as Error).message,
                    }
                  }
                } else {
                  result = resultBlob
                }
              } else {
                try {
                  result = (await response.json()) as FetchResult
                } catch (e) {
                  result = {
                    success: false,
                    error: e,
                    errorText: (e as Error).message,
                  }
                }
              }
            }

            if ('object' == typeof result && 'success' in result && !options.silence && !(result instanceof Blob)) {
              if (!result.success) {
                const res: FetchResult = result
                const errorName = () => {
                  if (messages.error) {
                    return messages.error
                  }
                  if (res.errorText && 'string' == typeof res.errorText) {
                    return res.errorText
                  }
                  return 'An error has occurred'
                }
                this.messageService.add({
                  severity: 'error',
                  summary: 'Request error',
                  detail: errorName(),
                  life: 10000,
                })
              } else if (messages.success) {
                this.messageService.add({
                  severity: 'success',
                  icon: 'pi pi-check-circle color-success',
                  summary: 'Successfully',
                  detail: messages.success,
                  life: 10000,
                })
              }
            } else if (result instanceof Blob) {
              result = {
                success: true,
                data: result,
              }
            }
            return result
          })()
        })
        .catch((error: unknown) => {
          this.messageService.add({
            severity: 'error',
            summary: messages.error || 'Request Error',
            detail: (error as Error).message,
            life: 10000,
          })
        }),
    }
  }
  post(url: string, data: Record<string, unknown> | FormData, options: Partial<Options> = {}) {
    return new Observable((observer) => {
      const formdata: FormData = data instanceof FormData ? data : new FormData()
      if (!(data instanceof FormData)) {
        for (const key in data) {
          formdata.append(key, data[key] as string | Blob)
        }
      }
      const config = {
        method: 'POST',
        body: formdata,
        headers: this._headers(options.headers || {}),
      }

      const request = this._request(
        url,
        config,
        Object.assign(
          {
            silence: false,
            parseMode: 'json' as ParseMode,
          },
          options
        )
      )

      request.response
        .then((result) => {
          observer.next(result)
        })
        .catch((e: unknown) => {
          observer.next({
            success: false,
            error: e,
          })
        })
        .finally(() => {
          observer.complete()
        })
    })
  }
  get(url: string, options: Partial<Options> = {}) {
    return new Observable((observer) => {
      const headers = Object.assign(
        {
          'Content-Type': 'application/json',
        },
        options.headers || {}
      )

      const config = {
        method: 'GET',
        headers: this._headers(headers),
      }

      const request = this._request(
        url,
        config,
        Object.assign(
          {
            silence: false,
            parseMode: 'json' as ParseMode,
          },
          options
        )
      )

      request.response
        .then((result) => {
          observer.next(result)
        })
        .catch((e: unknown) => {
          observer.next({
            success: false,
            error: e,
          })
        })
        .finally(() => {
          observer.complete()
        })
    })
  }
  postJSON(url: string, data: unknown, options: Partial<Options> = {}) {
    return new Observable((observer) => {
      const headers = Object.assign(
        {
          'Content-Type': 'application/json',
        },
        options.headers || {}
      )

      const config = {
        method: 'POST',
        body: JSON.stringify(data),
        headers: this._headers(headers),
      }

      const request = this._request(
        url,
        config,
        Object.assign(
          {
            silence: false,
            parseMode: 'json' as ParseMode,
          },
          options
        )
      )

      request.response
        .then((result) => {
          observer.next(result)
        })
        .catch((e: unknown) => {
          observer.next({
            success: false,
            error: e,
          })
        })
        .finally(() => {
          observer.complete()
        })
    })
  }
}
